import { useState } from 'react';
import _ from 'lodash';
import { Text, TextField, Box, Flex, IconButton, Button, Label, Chips, Icon, Divider } from '@vibe/core';
import { Link, Retry, IPRestrictions } from '@vibe/icons';
import { Heading } from '@vibe/core';
import { ExternalLink } from './ExternalLink';

function isValidSubdomain(domain: string) {
  const domainParts = domain.split('.');
  if (domainParts.length < 3) {
    return false;
  }

  return domainParts.every((part) => /^[a-z0-9-]+$/i.test(part));
}

// TODO: Add types
type CustomDomainSettingsProps = {
  registryEntries: any;
  triggerVerifyCustomDomain: any;
  areRegistryEntriesLoading: any;
  registryEntriesError: any;
  isVerifyingCustomDomain: any;
  verifyCustomDomainError: any;
  triggerConnectCustomDomain: any;
  isConnectCustomDomainConnecting: any;
  customDomainError: any;
  triggerDisconnectCustomDomain: any;
  isConnectCustomDomainDisconnecting: any;
  disconnectCustomDomainError: any;
};

export function CustomDomainSettings({
  registryEntries,
  triggerVerifyCustomDomain,
  areRegistryEntriesLoading,
  isVerifyingCustomDomain,
  triggerConnectCustomDomain,
  isConnectCustomDomainConnecting,
  triggerDisconnectCustomDomain,
  isConnectCustomDomainDisconnecting,
}: CustomDomainSettingsProps) {
  const [domainDraft, setDomainDraft] = useState<null | string>(null);
  const [statusState, setStatusState] = useState<boolean>(false);
  const customDomainEntry = _.find(registryEntries, { kind: 'custom' });
  const isLoading =
    areRegistryEntriesLoading || isVerifyingCustomDomain || isConnectCustomDomainConnecting || isConnectCustomDomainDisconnecting;

  let status = 'not_connected';

  if (isLoading) {
    status = 'loading';
  } else if (customDomainEntry) {
    status = customDomainEntry.verification_status;
  }

  return (
    <Box>
      <Box padding="xl" paddingTop="large" paddingBottom="large">
        <Flex direction="column" justify="start" align="start" gap={16}>
          <Flex gap={10}>
            <Icon icon={IPRestrictions} size="24" style={{ marginLeft: '-2px', transform: 'translate3d(0,-1px,0)' }} />
            <Heading type="h3" weight="bold" align="start">
              Custom Domain
            </Heading>
            {customDomainEntry && status === 'active' ? <Label text="Connected" color="primary" kind="fill" /> : null}
          </Flex>
          <Text type="text2" color="primary" ellipsis={false}>
            Connect your client portal to a custom subdomain, e.g. portal.domain.com and www.domain.com are both valid. You basically have
            to create a <strong>CNAME record</strong> in your domain's <strong>DNS settings</strong> that points to{' '}
            <strong>proxy.clientportalbuilder.com</strong>&nbsp; Learn how to{' '}
            <ExternalLink href="https://getgorilla.app/products/client-portal/docs/user-guides/custom-domain" target="_blank">
              connect a domain to your portal
            </ExternalLink>
            .
          </Text>
        </Flex>
      </Box>
      <Divider withoutMargin />
      <Box padding="xl" paddingTop="large" backgroundColor="greyBackgroundColor">
        <Flex gap={16} align="end" style={{ width: '100%' }}>
          <Box style={{ flex: 1, position: 'relative' }}>
            <TextField
              title="Domain"
              value={domainDraft !== null ? domainDraft : customDomainEntry?.domain}
              disabled={!!customDomainEntry}
              onChange={(newDomain) => {
                setDomainDraft(newDomain);
              }}
              onFocus={() => {
                setStatusState(true);
              }}
              onBlur={() => {
                setStatusState(false);
              }}
              placeholder="e.g. portal.domain.com"
              icon={Link}
              size="medium"
              className="custom__domain--input"
            />
            {(domainDraft && domainDraft !== null && isValidSubdomain(domainDraft)) || isLoading ? (
              <Box
                style={{
                  pointerEvents: 'none',
                  position: 'absolute',
                  right: '7px',
                  bottom: '8px',
                }}
              >
                <Label text={status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())} color="dark" kind="fill" />
              </Box>
            ) : null}
          </Box>
          <Flex gap={16}>
            {!customDomainEntry ? (
              <Button
                kind="primary"
                size="medium"
                color="primary"
                disabled={!domainDraft || !isValidSubdomain(domainDraft) || isLoading}
                onClick={() => {
                  triggerConnectCustomDomain(domainDraft);
                }}
              >
                Connect
              </Button>
            ) : (
              <Button
                kind="secondary"
                size="medium"
                color="primary"
                disabled={isLoading}
                onClick={() => {
                  triggerDisconnectCustomDomain();
                }}
              >
                Disconnect
              </Button>
            )}
            {customDomainEntry && status !== 'active' ? (
              <IconButton
                icon={Retry}
                kind="secondary"
                size="medium"
                color="primary"
                onClick={() => {
                  triggerVerifyCustomDomain();
                }}
                tooltipContent="Check status"
                tooltipProps={{
                  // @ts-ignore
                  position: 'right',
                }}
              />
            ) : null}
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
